import { useState, useRef, useEffect } from "react";
import { DataTable } from "../../components/DataTable";
import { useAuth } from "../../context/AuthContext";
import { blogService } from "../../services/BlogService";
import { CreateBlogRequest } from "../../interfaces/Blog";
import { TagService } from "../../services/TagService";
import { Tag } from "../../interfaces/Tags";

  
type Blog = {
  blogID: string;
  blogTitle: string;
  blogSlug: string;
  shortDescription: string;
  blogContent: string;
  blogImage: string;
  authorName: string;
 tags: string[];   // ← change here
  status: "Published" | "Draft" | "Archived";
  publishDate: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  canonicalUrl: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  createdDate: string;
};

const emptyForm = (): Omit<Blog, "blogID" | "createdDate"> => ({
  blogTitle: "",
  blogSlug: "",
  shortDescription: "",
  blogContent: "",
  blogImage: "",
  authorName: "",
  tags: [],
  status: "Draft",
  publishDate: new Date().toISOString().split("T")[0],
  metaTitle: "",
  metaDescription: "",
  metaKeywords: "",
  canonicalUrl: "",
  ogTitle: "",
  ogDescription: "",
  ogImage: "",
});

const API_PATH =
  process.env.REACT_APP_API_PATH;

/* ── Minimal Rich Text Editor ── */
function RichEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const editorRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
  if (editorRef.current && editorRef.current.innerHTML !== value) {
    editorRef.current.innerHTML = value || "";
  }
}, [value]);  

  function exec(cmd: string, val?: string) {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    onChange(editorRef.current?.innerHTML ?? "");
  }

  function handleInput() {
    onChange(editorRef.current?.innerHTML ?? "");
  }

  function handleImageInsert() {
    const url = prompt("Enter image URL:");
    if (url) exec("insertImage", url);
  }

  function handleLink() {
    const url = prompt("Enter link URL:");
    if (url) exec("createLink", url);
  }

  const btn = (label: string, cmd: string, title?: string, val?: string) => (
    <button type="button" className="blog-editor-btn" title={title ?? label}
      onMouseDown={e => { e.preventDefault(); exec(cmd, val); }}>
      {label}
    </button>
  );

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    const response = await blogService.uploadContentImage(formData);
    const imageUrl = `${API_PATH}/uploads/${response.data.data}`;

    // ONLY insert into editor
    exec("insertImage", imageUrl);

    onChange(editorRef.current?.innerHTML ?? "");
  }

  return (
    <div className="blog-editor-wrap">
      <div className="blog-editor-toolbar">
        <button type="button" className="blog-editor-btn fw-bold" title="Bold"
          onMouseDown={e => { e.preventDefault(); exec("bold"); }}><b>B</b></button>
        <button type="button" className="blog-editor-btn fst-italic" title="Italic"
          onMouseDown={e => { e.preventDefault(); exec("italic"); }}><i>I</i></button>
        <button type="button" className="blog-editor-btn" title="Underline"
          onMouseDown={e => { e.preventDefault(); exec("underline"); }}><u>U</u></button>
        <span className="blog-editor-sep"></span>
        {btn("H1", "formatBlock", "Heading 1", "<h1>")}
        {btn("H2", "formatBlock", "Heading 2", "<h2>")}
        {btn("H3", "formatBlock", "Heading 3", "<h3>")}
        {btn("P", "formatBlock", "Paragraph", "<p>")}
        <span className="blog-editor-sep"></span>
        <button type="button" className="blog-editor-btn" title="Bullet List"
          onMouseDown={e => { e.preventDefault(); exec("insertUnorderedList"); }}>• List</button>
        <button type="button" className="blog-editor-btn" title="Numbered List"
          onMouseDown={e => { e.preventDefault(); exec("insertOrderedList"); }}>1. List</button>
        <span className="blog-editor-sep"></span>
        <button type="button" className="blog-editor-btn" title="Align Left"
          onMouseDown={e => { e.preventDefault(); exec("justifyLeft"); }}>⬅</button>
        <button type="button" className="blog-editor-btn" title="Align Center"
          onMouseDown={e => { e.preventDefault(); exec("justifyCenter"); }}>≡</button>
        <button type="button" className="blog-editor-btn" title="Align Right"
          onMouseDown={e => { e.preventDefault(); exec("justifyRight"); }}>➡</button>
        <span className="blog-editor-sep"></span>
        <button type="button" className="blog-editor-btn" title="Insert Link"
          onMouseDown={e => { e.preventDefault(); handleLink(); }}>🔗 Link</button>
        <button
          type="button"
          className="blog-editor-btn"
          title="Insert Image"
          onMouseDown={e => {
            e.preventDefault();

            const choice = window.confirm(
              "OK = Upload Image\nCancel = Image URL"
            );

            if (choice) {
              imageInputRef.current?.click();
            } else {
              handleImageInsert();
            }
          }}
        >
          🖼 Img
        </button>

        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          className="d-none"
          onChange={handleImageUpload}
        />
        <button type="button" className="blog-editor-btn" title="Remove Formatting"
          onMouseDown={e => { e.preventDefault(); exec("removeFormat"); }}>Tx</button>
        <button type="button" className="blog-editor-btn" title="Undo"
          onMouseDown={e => { e.preventDefault(); exec("undo"); }}>↩</button>
        <button type="button" className="blog-editor-btn" title="Redo"
          onMouseDown={e => { e.preventDefault(); exec("redo"); }}>↪</button>
      </div>
      <div
        ref={editorRef}
        className="blog-editor-content"
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        data-placeholder="Write your blog content here..."
      />
    </div>
  );
}


export function CreateBlogPage() {
  const { user } = useAuth();
  const canManage = user?.role === "admin";

  const [form, setForm] = useState(emptyForm());
  const [saved, setSaved] = useState(false);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");

  const [selectedBlogFile, setSelectedBlogFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [viewBlog, setViewBlog] = useState<Blog | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [editingBlogId, setEditingBlogId] = useState<string | null>(null);

  const [tagsList, setTagsList] = useState<Tag[]>([]);

  const [showTagsDropdown, setShowTagsDropdown] = useState(false);

  function hf<K extends keyof typeof form>(key: K, val: typeof form[K]) {
    setForm(prev => {
      const next = { ...prev, [key]: val };
      if (key === "blogTitle") {
        next.blogSlug = (val as string).toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
      }
      return next;
    });
  }

  async function loadTags() {
  try {
    const data = await TagService.getAll();

    setTagsList(data);
  } catch (error) {
    console.log(error);
  }
}
async function loadBlogs() {
  try {
    const data = await blogService.getAll();

    const formatted = data.map((b: any) => ({
      ...b,

      blogID: b.blogID || b.blogId,

      blogImage: b.blogImage
        ? `${API_PATH}/uploads/${b.blogImage}`
        : "",

      createdDate:
        b.createdDate ??
        b.createdAt ??
        "",

      tags:
        Array.isArray(b.tags)
          ? b.tags
          : typeof b.tags === "string"
          ? b.tags
              .replace("[", "")
              .replace("]", "")
              .split(",")
              .map((x:string) => x.trim())
              .filter(Boolean)
          : []
    }));

    setBlogs(formatted);

  } catch (error) {
    console.log("Load blog failed:", error);
  }
}

  async function uploadCoverImage(file: File) {
    const formData = new FormData();
    formData.append("file", file);

    const res = await blogService.uploadContentImage(formData);

    const url = `${API_PATH}/uploads/${res.data.data}`;

    setSelectedBlogFile(file);
    setImagePreview(url);
  }

  function formatDate(dateStr?: string) {
    if (!dateStr) return "-";

    const date = new Date(dateStr);

    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).format(date);
  }

  useEffect(() => {
    loadBlogs();
    loadTags();
  }, []);

  useEffect(() => {
  const closeDropdown = () => {
    setShowTagsDropdown(false);
  };

  document.addEventListener(
    "click",
    closeDropdown
  );

  return () => {
    document.removeEventListener(
      "click",
      closeDropdown
    );
  };
}, []);
  function handleImageFile(
    e: React.ChangeEvent<HTMLInputElement>
  ) {
    const file = e.target.files?.[0];

    if (!file) return;

    // Store actual file for API
    setSelectedBlogFile(file);

    // Preview image
    const reader = new FileReader();

    reader.onload = ev => {
      const url = ev.target?.result as string;

      setImagePreview(url);

      setForm(prev => ({
        ...prev,
        blogImage: url
      }));
    };

    reader.readAsDataURL(file);
  }
async function handleSubmit() {
  try {
    const payload = {
      ...form,
      blogImage: selectedBlogFile ?? undefined,
    };

    if (editingId) {
      await blogService.update(
        editingId,
        payload
      );

      alert("Blog updated successfully");
    } else {
      await blogService.create(
        payload
      );

      alert("Blog created successfully");
    }

    // Reset form immediately
    setForm(emptyForm());
    setSelectedBlogFile(null);
    setImagePreview("");
    setEditingId(null);

  } catch (error) {
    console.log(
      "Save error:",
      error
    );

    alert("Failed to save blog");
    return;
  }

  // Refresh separately
  try {
    await loadBlogs();
  } catch (error) {
    console.log(
      "Refresh error:",
      error
    );
  }
}
 function startEdit(b: Blog) {
  setEditingId(b.blogID);

  setForm({
    blogTitle: b.blogTitle ?? "",
    blogSlug: b.blogSlug ?? "",
    shortDescription: b.shortDescription ?? "",
    blogContent: b.blogContent ?? "",
    blogImage: b.blogImage ?? "",
    authorName: b.authorName ?? "",
     tags: Array.isArray(b.tags)
      ? b.tags
      : [],
    status: b.status ?? "Draft",
     publishDate: b.publishDate
      ? b.publishDate.split("T")[0]
      : "",


    metaTitle: b.metaTitle ?? "",
    metaDescription: b.metaDescription ?? "",
    metaKeywords: b.metaKeywords ?? "",
    canonicalUrl: b.canonicalUrl ?? "",
    ogTitle: b.ogTitle ?? "",
    ogDescription: b.ogDescription ?? "",
    ogImage: b.ogImage ?? "",
  });

  setImagePreview(b.blogImage || "");
  setSelectedBlogFile(null);

  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
}

  function cancelEdit() { setEditingId(null); }

  async function deleteBlog() {
    if (!deleteConfirmId) return;

    try {
      await blogService.delete(deleteConfirmId);

      alert("Blog deleted successfully");

      setBlogs(prev =>
        prev.filter(b => b.blogID !== deleteConfirmId)
      );

      setDeleteConfirmId(null);
    } catch (error) {
      console.log(error);
      alert("Failed to delete blog");
    }
  }
  function viewBlogItem(blog: Blog) {
    setViewBlog(blog);
  }
  function toggleStatus(id: string) {
    setBlogs(prev => prev.map(b => b.blogID === id
      ? { ...b, status: b.status === "Published" ? "Draft" : "Published" } : b));
  }

  const statusBadge: Record<Blog["status"], string> = {
    Published: "text-bg-success",
    Draft: "text-bg-warning",
    Archived: "text-bg-secondary",
  };

  return (
    <div className="d-grid gap-4">
      <div className="card border-0 shadow-sm mst-card">
        <div className="card-body p-4">
          <div className="d-flex align-items-center gap-2 mb-4">
            <div className="mst-icon-box"><i className="bi bi-pencil-square"></i></div>
            <div>
              <h5 className="mb-0 fw-bold mst-title">Create Blog</h5>
              <small className="mst-subtitle">Write and publish a new blog post</small>
            </div>
          </div>

          {saved && (
            <div className="alert alert-success d-flex align-items-center gap-2 py-2 mb-3 mst-alert">
              <i className="bi bi-check-circle-fill"></i> Blog saved successfully!
            </div>
          )}

          <div className="row g-3">
            {/* Blog Title */}
            <div className="col-md-8">
              <label className="form-label fw-semibold mst-label">Blog Title <span className="text-danger">*</span></label>
              <input className="form-control mst-control" placeholder="e.g. Top 10 Jewellery Trends 2025"
                value={form.blogTitle} onChange={e => hf("blogTitle", e.target.value)} />
            </div>

            {/* Blog Slug */}
            <div className="col-md-4">
              <label className="form-label fw-semibold mst-label">Blog Slug (auto-generated)</label>
              <div className="input-group">
                <span className="input-group-text mst-ig-text">/blog/</span>
                <input className="form-control mst-control-right" placeholder="top-10-trends"
                  value={form.blogSlug} onChange={e => hf("blogSlug", e.target.value)} />
              </div>
            </div>

            {/* Author */}
            <div className="col-md-4">
              <label className="form-label fw-semibold mst-label">Author Name</label>
              <input className="form-control mst-control" placeholder="e.g. Priya Sharma"
                value={form.authorName} onChange={e => hf("authorName", e.target.value)} />
            </div>

            {/* Status */}
            <div className="col-md-4">
              <label className="form-label fw-semibold mst-label">Status</label>
              <select className="form-select mst-select" value={form.status}
                onChange={e => hf("status", e.target.value as Blog["status"])}>
                <option value="Draft">Draft</option>
                <option value="Published">Published</option>
                <option value="Archived">Archived</option>
              </select>
            </div>

            {/* Publish Date */}
            <div className="col-md-4">
              <label className="form-label fw-semibold mst-label">Publish Date</label>
              <input type="date" className="form-control mst-control"
                value={form.publishDate} onChange={e => hf("publishDate", e.target.value)} />
            </div>

            {/* Tags */}
  {/* Tags */}
<div className="col-12">
  <label className="form-label fw-semibold mst-label">
    Tags
  </label>

  <div className="position-relative">

    {/* Selected tags area */}
    <div
  className="form-control d-flex flex-wrap align-items-start gap-2"
  style={{
    minHeight: "45px",
    height: "auto",
    overflowY: "auto",
    padding: "8px",
    cursor: "pointer",
    whiteSpace: "normal"
  }}
  onClick={(e) => {
    e.stopPropagation();
    setShowTagsDropdown(prev => !prev);
  }}
>
 {form.tags.length ? (
  form.tags.map(tag => (
      <span
        key={tag}
        className="badge bg-primary d-flex align-items-center"
        style={{
          padding: "8px 10px",
          marginBottom: "4px"
        }}
      >
        {tag}

        <button
          type="button"
          className="btn-close btn-close-white ms-2"
          style={{
            fontSize: "8px"
          }}
          onClick={(e) => {
            e.stopPropagation();

            const updated =
  form.tags.filter(
    t => t !== tag
  );

hf("tags", updated);
          }}
        />
      </span>
    ))
  ) : (
    <span className="text-muted">
      Select tags...
    </span>
  )}
</div>

    {/* Dropdown */}
    {/* Dropdown */}
{showTagsDropdown && (
  <div
    className="card position-absolute w-100 mt-1 p-2"
    style={{
      zIndex: 1000,
      maxHeight: "200px",
      overflowY: "auto"
    }}
    onClick={(e) => e.stopPropagation()} // prevent closing
  >
    {tagsList.map(tag => {
      const selected =
  form.tags.includes(
    tag.tagName
  );

      return (
        <div
          key={tag.tagID}
          className="form-check py-1"
        >
          <input
            id={`tag-${tag.tagID}`}
            type="checkbox"
            className="form-check-input"
            checked={selected}
            onChange={(e) => {
              let updated =
  [...form.tags];

              if (e.target.checked) {
                updated = [...updated, tag.tagName];
              } else {
                updated = updated.filter(
                  t => t !== tag.tagName
                );
              }

              hf("tags", updated);
            }}
          />

          <label
            htmlFor={`tag-${tag.tagID}`}
            className="form-check-label ms-2"
            style={{ cursor: "pointer" }}
          >
            {tag.tagName}
          </label>
        </div>
      );
    })}
  </div>
)}
  </div>
</div>

            {/* Short Description */}
            <div className="col-12">
              <label className="form-label fw-semibold mst-label">Short Description</label>
              <textarea className="form-control mst-textarea" rows={2}
                placeholder="Brief summary shown in listing pages..." maxLength={200}
                value={form.shortDescription} onChange={e => hf("shortDescription", e.target.value)} />
              <small className="mst-seo-counter">{form.shortDescription.length}/200</small>
            </div>

            {/* Blog Content — Rich Editor */}
            <div className="col-12">
              <label className="form-label fw-semibold mst-label">Blog Content</label>
              <RichEditor value={form.blogContent} onChange={v => hf("blogContent", v)} />
            </div>

            {/* Blog Image */}
            <div className="col-12">
              <label className="form-label fw-semibold mst-label">Blog Image</label>
              <div className="usr-img-upload-row">
                {imagePreview ? (
                  <div className="blog-img-preview">
                    <img src={imagePreview} alt="Blog cover" />
                    <button type="button" className="usr-img-remove"
                      onClick={() => { setImagePreview(""); setForm(p => ({ ...p, blogImage: "" })); }}>
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
                ) : (
                  <div className="blog-img-placeholder">
                    <i className="bi bi-image"></i>
                    <span>No image selected</span>
                  </div>
                )}
                <div className="usr-img-controls">
                  <button type="button" className="btn mst-btn-reset"
                    onClick={() => fileInputRef.current?.click()}>
                    <i className="bi bi-upload me-2"></i>Upload Image
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*"
                    className="d-none" onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadCoverImage(file);
                    }} />
                  <small className="d-block text-muted mt-1">or paste image URL</small>
                  <input className="form-control mst-control mt-1" placeholder="https://example.com/blog-cover.jpg"
                    value={form.blogImage.startsWith("data:") ? "" : form.blogImage}
                    onChange={e => { hf("blogImage", e.target.value); setImagePreview(e.target.value); }} />
                </div>
              </div>
            </div>

            {/* ── SEO ── */}
            <div className="col-12 mt-2">
              <div className="mst-seo-divider"><i className="bi bi-search me-2"></i>SEO & Meta Settings</div>
            </div>
            <div className="col-12">
              <div className="mst-seo-hint"><i className="bi bi-info-circle me-1"></i>These fields control how this blog post appears in Google search results and social media.</div>
            </div>

            <div className="col-md-8">
              <label className="form-label fw-semibold mst-label">Meta Title <span className="mst-seo-counter">{form.metaTitle.length}/60</span></label>
              <input className="form-control mst-control" placeholder="e.g. Top 10 Jewellery Trends | NineSisters" maxLength={60}
                value={form.metaTitle} onChange={e => hf("metaTitle", e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold mst-label">Meta Keywords</label>
              <input className="form-control mst-control" placeholder="e.g. jewellery, trends, gold"
                value={form.metaKeywords} onChange={e => hf("metaKeywords", e.target.value)} />
            </div>
            <div className="col-12">
              <label className="form-label fw-semibold mst-label">Meta Description <span className="mst-seo-counter">{form.metaDescription.length}/160</span></label>
              <textarea className="form-control mst-textarea" rows={2} maxLength={160}
                placeholder="Brief summary shown in search results — 120–160 characters recommended"
                value={form.metaDescription} onChange={e => hf("metaDescription", e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">Canonical URL <span className="mst-seo-opt">(optional)</span></label>
              <input className="form-control mst-control" placeholder="https://ninesistersjewels.com/blog/..."
                value={form.canonicalUrl} onChange={e => hf("canonicalUrl", e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">OG Image URL <span className="mst-seo-opt">(Social share image)</span></label>
              <input className="form-control mst-control" placeholder="https://..."
                value={form.ogImage} onChange={e => hf("ogImage", e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">OG Title <span className="mst-seo-opt">(optional)</span></label>
              <input className="form-control mst-control" placeholder="e.g. Jewellery Trends 2025 | NineSisters"
                value={form.ogTitle} onChange={e => hf("ogTitle", e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">OG Description <span className="mst-seo-opt">(optional)</span></label>
              <input className="form-control mst-control" placeholder="Social media preview description"
                value={form.ogDescription} onChange={e => hf("ogDescription", e.target.value)} />
            </div>
          </div>

          <div className="d-flex gap-2 mt-4">
            <button type="button" className="btn btn-primary px-4 mst-btn-save"
              onClick={handleSubmit} disabled={!form.blogTitle.trim()}>
              <i className="bi bi-plus-circle me-2"></i>  {editingId ? "Update Blog" : "Save Blog"}
            </button>
            <button type="button" className="btn mst-btn-reset"
              onClick={() => { setForm(emptyForm()); setImagePreview(""); }}>
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* ── TABLE ── */}
      <DataTable<Blog>
        title="Blogs"
        rows={blogs}
        rowKey={r => r.blogID}
        searchPlaceholder="Search blogs..."
        exportFileName="blogs"
        columns={[
          {
            key: "blogTitle", label: "Blog", sortable: true,
            render: r => (
              <div className="d-flex align-items-center gap-2">
                {r.blogImage && (
                  <img src={r.blogImage} alt={r.blogTitle}
                    style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />
                )}
                <div>
                  <div className="fw-semibold" style={{ fontSize: 13 }}>{r.blogTitle}</div>
                  <code className="mst-code">/blog/{r.blogSlug}</code>
                </div>
              </div>
            ),
          },
          { key: "authorName", label: "Author" },
          {
  key: "tags",
  label: "Tags",
  render: r =>
    r.tags.length
      ? (
          <small>
            {r.tags.join(", ")}
          </small>
        )
      : (
          <span>—</span>
        )
},  
          {
            key: "publishDate",
            label: "Publish Date",
            sortable: true,
            render: r => formatDate(r.publishDate),
          },
          {
            key: "status", label: "Status",
            render: r => <span className={`badge ${statusBadge[r.status]}`}>{r.status}</span>
          },
          {
            key: "createdDate",
            label: "Created",
            sortable: true,
            render: r => formatDate(r.createdDate),
          },

          {
            key: "actions",
            label: "Actions",
            className: "text-end",
            render: (r: Blog) => (
              <div className="d-flex gap-2">

                {/* VIEW */}


                {/* EDIT */}
                <button
                  className="btn btn-primary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    startEdit(r);
                  }}
                >
                  <i className="bi bi-pencil-square"></i>
                </button>



                {/* DELETE */}
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => setDeleteConfirmId(r.blogID)}
                >
                  <i className="bi bi-trash"></i>
                </button>

              </div>
            )
          }

        ]}
      />
      {deleteConfirmId && (
        <div
          className="pm-del-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setDeleteConfirmId(null);
            }
          }}
        >
          <div className="pm-del-box">

            <div className="pm-del-title">
              Delete Blog?
            </div>

            <div className="pm-del-sub">
              This action cannot be undone.
            </div>

            <div className="pm-del-btns">
              <button
                className="pm-btn-ghost"
                onClick={() => setDeleteConfirmId(null)}
              >
                Cancel
              </button>

              <button
                className="pm-btn-danger"
                onClick={deleteBlog}
              >
                Delete
              </button>
            </div>

          </div>
        </div>
      )}
      {viewBlog && (
        <div
          className="pm-del-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setViewBlog(null);
            }
          }}
        >
          <div
            className="pm-del-box"
            style={{ maxWidth: "800px", width: "100%" }}
          >

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">Blog Details</h4>

              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setViewBlog(null)}
              >
                Close
              </button>
            </div>

            {viewBlog.blogImage && (
              <img
                src={viewBlog.blogImage}
                alt={viewBlog.blogTitle}
                style={{
                  width: "100%",
                  borderRadius: 10,
                  marginBottom: 10
                }}
              />
            )}

            <div className="row g-3">

              <div className="col-md-6">
                <strong>Title:</strong>
                <div>{viewBlog.blogTitle}</div>
              </div>

              <div className="col-md-6">
                <strong>Slug:</strong>
                <div>{viewBlog.blogSlug}</div>
              </div>

              <div className="col-md-6">
                <strong>Author:</strong>
                <div>{viewBlog.authorName}</div>
              </div>

              <div className="col-md-6">
                <strong>Status:</strong>
                <div>{viewBlog.status}</div>
              </div>

              <div className="col-12">
                <strong>Short Description:</strong>
                <div>{viewBlog.shortDescription}</div>
              </div>

              <div className="col-12">
                <strong>Content:</strong>
                <div
                  dangerouslySetInnerHTML={{
                    __html: viewBlog.blogContent
                  }}
                />
              </div>

            </div>

          </div>
        </div>
      )}


    </div>
  );
}
